// Day Care setup (daycare_admin): a unit is a ward whose beds are its
// machines/chairs, the services it offers, its shifts, its session chart and
// its per-session package; each machine is tagged with an infection group.
// The ward and its beds themselves are created in the existing bed screens.

import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";

import DaycareScreen from "../components/DaycareScreen";
import ToneChip from "../components/ToneChip";
import { createUnit, fetchUnits, saveMachine, searchIpdServices, updateUnit } from "../api/daycareApi";
import { fetchIpdBedTypes, fetchIpdBeds } from "../../ipd/pages/patient-admission-ipd/api/registrationService";
import { listPackages } from "../../ipd-billing/package-billing-api";
import {
  IPD_COLORS,
  IpdButton,
  IpdDetailRow,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
} from "../../ipd/utils";
import { CHART_TYPE, CHART_TYPES, GROUP_CLEAN, VIRAL_GROUPS } from "../constants";
import { groupLabel } from "../utils/sessionView";

const EMPTY_SHIFT = { code: "", label: "", start: "", end: "" };
const HEAD_SX = {
  fontSize: 11.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase",
  color: IPD_COLORS.textSecondary, bgcolor: IPD_COLORS.panelHeaderBg,
};

function unitForm(unit) {
  return {
    name: unit?.name || "",
    code: unit?.code || "",
    bed_type_code: unit?.bed_type_code || "",
    chart_type: unit?.chart_type || CHART_TYPE.NONE,
    shifts: unit?.shifts?.length ? unit.shifts : [{ ...EMPTY_SHIFT }],
    services: unit?.services || [],
    retest_days: String(unit?.retest_days || 90),
    package_id: unit?.package_id || "",
    active: unit ? unit.active : true,
  };
}

function ServicePicker({ value, onChange }) {
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState("");

  useEffect(() => {
    if (input.trim().length < 2) return undefined;
    const handle = window.setTimeout(() => {
      searchIpdServices(input).then((rows) =>
        setOptions(rows.map((r) => ({ medical_service_id: r.medical_service_id, name: r.term || r.name }))));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [input]);

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={value}
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(a, b) => a.medical_service_id === b.medical_service_id}
      getOptionLabel={(o) => o.name || ""}
      onInputChange={(_, next) => setInput(next)}
      onChange={(_, next) => onChange(next)}
      renderInput={(params) => <IpdTextField {...params} placeholder="Type 2+ letters to search the IPD service master" />}
    />
  );
}

function UnitDialog({ unit, wards, packages, onClose, onSaved }) {
  const [form, setForm] = useState(() => unitForm(unit));
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const setShift = (index, key, value) =>
    setForm((f) => ({ ...f, shifts: f.shifts.map((s, i) => (i === index ? { ...s, [key]: value } : s)) }));

  const save = async () => {
    setBusy(true);
    const body = {
      name: form.name,
      code: form.code,
      chart_type: form.chart_type,
      shifts: form.shifts,
      service_ids: form.services.map((s) => s.medical_service_id),
      retest_days: form.retest_days,
      package_id: form.package_id || null,
      active: form.active,
    };
    if (!unit) body.bed_type_code = form.bed_type_code;
    const res = unit ? await updateUnit(unit.id, body) : await createUnit(body);
    setBusy(false);
    if (!res.ok) {
      toast.error(res.message || "Could not save the unit");
      return;
    }
    toast.success(res.message || "Unit saved");
    onSaved();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{unit ? `Edit ${unit.name}` : "New day care unit"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
          <IpdFormField label="Name" required sx={{ gridColumn: { md: "span 2" } }}>
            <IpdTextField fullWidth value={form.name} onChange={set("name")} />
          </IpdFormField>
          <IpdFormField label="Code" required>
            <IpdTextField fullWidth value={form.code} onChange={set("code")} />
          </IpdFormField>
          <IpdFormField label="Session chart">
            <IpdTextField select fullWidth value={form.chart_type} onChange={set("chart_type")}>
              {CHART_TYPES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </IpdTextField>
          </IpdFormField>
          <IpdFormField label="Ward (its beds are the machines)" required
            helperText={unit ? "A unit's ward cannot change" : ""} sx={{ gridColumn: { md: "span 2" } }}>
            <IpdTextField select fullWidth value={form.bed_type_code} onChange={set("bed_type_code")} disabled={Boolean(unit)}>
              {wards.map((w) => (
                <MenuItem key={w.bedTypeCode} value={w.bedTypeCode}>{`${w.bedTypeName} (${w.bedTypeCode})`}</MenuItem>
              ))}
            </IpdTextField>
          </IpdFormField>
          <IpdFormField label="Per-session package" sx={{ gridColumn: { md: "span 2" } }}>
            <IpdTextField select fullWidth value={form.package_id} onChange={set("package_id")} SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">None — bill the session service</MenuItem>
              {packages.map((p) => <MenuItem key={p.id} value={p.id}>{`${p.name} (${p.code})`}</MenuItem>)}
            </IpdTextField>
          </IpdFormField>
          <IpdFormField label="Services this unit offers" required sx={{ gridColumn: { md: "span 4" } }}>
            <ServicePicker value={form.services} onChange={(services) => setForm((f) => ({ ...f, services }))} />
          </IpdFormField>
          <IpdFormField label="Marker retest interval (days)">
            <IpdTextField fullWidth type="number" value={form.retest_days} onChange={set("retest_days")} />
          </IpdFormField>
          {unit && (
            <FormControlLabel sx={{ alignSelf: "end", mb: 0.5 }} label="Unit active"
              control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />} />
          )}
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: 13.5, mt: 3, mb: 1, color: IPD_COLORS.textPrimary }}>Shifts</Typography>
        <Box sx={{ border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_SX}>Code</TableCell>
                <TableCell sx={HEAD_SX}>Label</TableCell>
                <TableCell sx={HEAD_SX}>Start</TableCell>
                <TableCell sx={HEAD_SX}>End</TableCell>
                <TableCell sx={HEAD_SX} />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.shifts.map((shift, index) => (
                // eslint-disable-next-line react/no-array-index-key -- a new shift has no code yet
                <TableRow key={index}>
                  <TableCell><IpdTextField fullWidth value={shift.code} inputProps={{ "aria-label": "Shift code" }}
                    onChange={(e) => setShift(index, "code", e.target.value)} /></TableCell>
                  <TableCell><IpdTextField fullWidth value={shift.label} inputProps={{ "aria-label": "Shift label" }}
                    onChange={(e) => setShift(index, "label", e.target.value)} /></TableCell>
                  <TableCell><IpdTextField fullWidth type="time" value={shift.start} inputProps={{ "aria-label": "Start" }}
                    onChange={(e) => setShift(index, "start", e.target.value)} /></TableCell>
                  <TableCell><IpdTextField fullWidth type="time" value={shift.end} inputProps={{ "aria-label": "End" }}
                    onChange={(e) => setShift(index, "end", e.target.value)} /></TableCell>
                  <TableCell padding="none">
                    <IconButton aria-label="Remove shift" disabled={form.shifts.length <= 1}
                      onClick={() => setForm((f) => ({ ...f, shifts: f.shifts.filter((_, i) => i !== index) }))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <IpdButton size="small" variant="outlined" sx={{ mt: 1.5 }}
          onClick={() => setForm((f) => ({ ...f, shifts: [...f.shifts, { ...EMPTY_SHIFT }] }))}>
          Add shift
        </IpdButton>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <IpdButton variant="outlined" onClick={onClose}>Close</IpdButton>
        <IpdButton loading={busy} disabled={busy} onClick={save}>Save unit</IpdButton>
      </DialogActions>
    </Dialog>
  );
}

function MachineRow({ unitId, bedCode, machine, onSaved }) {
  const [group, setGroup] = useState(machine?.viral_group || GROUP_CLEAN);
  const [active, setActive] = useState(machine ? machine.active : false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const res = await saveMachine(unitId, { bed_code: bedCode, viral_group: group, active });
    setBusy(false);
    if (!res.ok) {
      toast.error(res.message || "Could not save the machine");
      return;
    }
    toast.success(res.message || "Machine saved");
    onSaved();
  };

  const changed = !machine || machine.viral_group !== group || machine.active !== active;
  return (
    <TableRow hover>
      <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>{bedCode}</TableCell>
      <TableCell sx={{ minWidth: 180 }}>
        <IpdTextField select fullWidth value={group} inputProps={{ "aria-label": `Infection group of ${bedCode}` }}
          onChange={(e) => setGroup(e.target.value)}>
          {VIRAL_GROUPS.map((g) => <MenuItem key={g} value={g}>{groupLabel(g)}</MenuItem>)}
        </IpdTextField>
      </TableCell>
      <TableCell>
        <FormControlLabel label={active ? "In use" : "Not a machine"} sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
          control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />} />
      </TableCell>
      <TableCell align="right">
        <IpdButton size="small" variant="outlined" loading={busy} disabled={busy || !changed} onClick={save}>Save</IpdButton>
      </TableCell>
    </TableRow>
  );
}

function UnitMachines({ unit, onSaved }) {
  const [beds, setBeds] = useState([]);

  useEffect(() => {
    fetchIpdBeds(unit.bed_type_code).then(setBeds).catch(() => setBeds([]));
  }, [unit.bed_type_code]);

  const byBed = Object.fromEntries((unit.machines || []).map((m) => [m.bed_code, m]));
  if (!beds.length) {
    return <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>This ward has no beds yet.</Typography>;
  }
  return (
    <Box sx={{ border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1, overflow: "hidden" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD_SX}>Bed</TableCell>
            <TableCell sx={HEAD_SX}>Infection group</TableCell>
            <TableCell sx={HEAD_SX}>Machine</TableCell>
            <TableCell sx={HEAD_SX} />
          </TableRow>
        </TableHead>
        <TableBody>
          {beds.map((bed) => (
            <MachineRow key={bed.bedNo} unitId={unit.id} bedCode={bed.bedNo} machine={byBed[bed.bedNo]} onSaved={onSaved} />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function DaycareSetup() {
  const [units, setUnits] = useState([]);
  const [wards, setWards] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Only the first load blanks the screen; a refresh after a save keeps the
  // page (and any edits in progress on other machine rows) in place.
  const load = useCallback(async () => {
    setError("");
    try {
      setUnits(await fetchUnits({ include_inactive: true }));
    } catch (err) {
      setError(err?.body?.message || "Could not load the day care units");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    fetchIpdBedTypes().then(setWards).catch(() => setWards([]));
    listPackages({ status: "ACTIVE", activeOnly: true }).then(setPackages).catch(() => setPackages([]));
  }, [load]);

  return (
    <DaycareScreen
      title="Unit setup"
      description="Units, their shifts and services, the per-session package, and each machine's infection group."
      loading={loading}
      error={error}
      onRetry={load}
      actions={<IpdButton onClick={() => setEditing(null)}>New unit</IpdButton>}
    >
      {units.length === 0 && (
        <IpdSectionCard title="No day care unit yet">
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
            Create the ward and its beds in the bed screens first, then add the unit here.
          </Typography>
        </IpdSectionCard>
      )}
      {units.map((unit) => {
        const pkg = packages.find((p) => p.id === unit.package_id);
        return (
          <IpdSectionCard
            key={unit.id}
            title={`${unit.name} (${unit.code})`}
            subtitle={`Ward ${unit.bed_type_code}`}
            actions={(
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {!unit.active && <ToneChip label="Inactive" />}
                <IpdButton size="small" variant="outlined" onClick={() => setEditing(unit)}>Edit unit</IpdButton>
              </Box>
            )}
            sx={{ mb: 2.5 }}
          >
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 4, mb: 1 }}>
              <IpdDetailRow label="Session chart" value={CHART_TYPES.find((c) => c.value === unit.chart_type)?.label} />
              <IpdDetailRow label="Shifts" value={(unit.shifts || []).map((s) => `${s.label} ${s.start}–${s.end}`).join(", ")} />
              <IpdDetailRow label="Services" value={(unit.services || []).map((s) => s.name).join(", ") || "None"} />
              <IpdDetailRow label="Billing" value={pkg ? `Package: ${pkg.name}` : "Session service charged at check-in"} />
              <IpdDetailRow label="Marker retest" value={`Every ${unit.retest_days} days`} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 13.5, mb: 1, color: IPD_COLORS.textPrimary }}>Machines</Typography>
            <UnitMachines unit={unit} onSaved={load} />
          </IpdSectionCard>
        );
      })}
      {editing !== undefined && (
        <UnitDialog
          unit={editing}
          wards={wards}
          packages={packages}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }}
        />
      )}
    </DaycareScreen>
  );
}
